import { ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { enumService } from "@/common/services/enum";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { MaintenanceDto, MaintenanceGetPaginatedDto } from "@/core/api/generated/v1";

import { getVehicleOptionTitleForOperation } from "../Vehicle/VehicleAutocomplete";
import VehicleInline from "../Vehicle/VehicleInline";

export type MaintenanceAutocompleteOption = BaseAutocompleteOption<MaintenanceDto>;

export const getMaintenanceOptionTitle = (data: MaintenanceDto): string =>
  StringHelper.join(
    [
      data.name,
      data.vehicle ? getVehicleOptionTitleForOperation(data.vehicle) : data.localNumber,
      data?.stageHistory &&
        enumService.getEnumValueName("MaintenanceStage", data?.stageHistory?.stage),
    ],
    " | ",
    {
      skipEmpty: true,
    },
  );

export const maintenanceToOption = (data: MaintenanceDto): MaintenanceAutocompleteOption => ({
  id: data.id!,
  title: getMaintenanceOptionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface MaintenanceAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<MaintenanceDto> {
  searchFilters?: Partial<Omit<MaintenanceGetPaginatedDto, "search" | "includeIds">>;
}

export default function MaintenanceAutocomplete({
  searchFilters,
  isPreload = true,
  ...otherProps
}: MaintenanceAutocompleteProps) {
  console.log("searchFilters", searchFilters);
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={maintenanceToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.maintenancesApi.apiV1MaintenancesGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          maintenanceGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          maintenanceGetPaginatedDto: {
            ...params.maintenanceGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Maintenance'
      placeholder='Search...'
      renderOption={(props, option: MaintenanceAutocompleteOption) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='maintenance' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      {option?.data?.vehicle && (
                        <VehicleInline entity={option.data.vehicle} withIcon={false} />
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
